// src/oura/components/newscan/TellOura.tsx
//
// "Tell Oura" above the Review's cards: a short conversation. Staff say what
// is wrong in their own words; Oura answers with the changes it would make,
// each with what it touches below (hovering one outlines those cards). Staff
// untick any they do not want and press Apply; the changes then run as steps
// that tick in turn, the panel held where it is on screen, and Oura says what
// changed, with one Undo for all of it. Nothing below moves before Apply.
//
// The reading is newscan/tellOura.ts, a stand-in for the backend's model.
//
// On the two-panel Review it is docked (TellOuraDock): a "Tell Oura" button at
// the bottom right opens it as a chat panel over the page, so the cards keep
// their width and the conversation keeps its own (Jovi, 20 Oct). The panel
// stays mounted while closed, so the conversation and an Undo survive it.

import { ArrowUpIcon, CheckIcon, SparklesIcon, XIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { ScanDraft } from "@service/aeo/scanDraftTypes";
import { cn } from "@oura/lib/cn";
import { FOCUS_RING, PillButton, SURFACE, TYPE } from "@oura/ui";
import { plural } from "@oura/newscan/copy";
import { askOura, tellSuggestions, type TellChange, type TellReading } from "@oura/newscan/tellOura";
import { useWritesPaused } from "@context/maintenance";
import { ApplySteps, Tick, useHoldInPlace, type ApplyOp, type Applier } from "./BranchesCard";

const COPY = {
    title: "Tell Oura",
    placeholder: "Tell Oura what is wrong",
    send: "Send",
    reading: "Reading",
    apply: (n: number) => (n === 1 ? "Apply this change" : `Apply ${n} changes`),
    notQuite: "Not quite",
    notQuiteReply: "OK, nothing changed. Say it another way and I'll read it again.",
    working: "Updating the 50",
    done: "Done.",
    left: (before: number, after: number) =>
        after === 0 ? `Nothing left to decide, down from ${before}.` : `${plural(after, "decision")} left, down from ${before}.`,
    undo: "Undo all of it",
    undone: "Undone. Everything is back as it was.",
    stuck: "I couldn't finish that. Nothing after the last tick changed. Check the cards below.",
    show: "Show",
    dockedPlaceholder: "Say what Oura got wrong",
    intro: "Say what Oura got wrong in your own words. It can change the branches, the core service and the plan's keywords. Nothing changes until you press Apply.",
    open: "Tell Oura",
    close: "Close Tell Oura",
    dockSub: "Branches, core service, keywords",
} as const;

type Turn =
    | { id: number; who: "you"; text: string }
    | { id: number; who: "oura"; state: "reading" }
    | { id: number; who: "oura"; state: "proposal"; reading: TellReading; picked: string[] }
    | { id: number; who: "oura"; state: "applying"; changes: TellChange[] }
    | { id: number; who: "oura"; state: "done"; changes: TellChange[]; before: number; after: number }
    | { id: number; who: "oura"; state: "note"; text: string; suggestions?: string[] };

/** Outlines the cards a change touches, and scrolls the first into view on Show. */
function highlight(touches: string[] | null, scroll = false) {
    document.querySelectorAll<HTMLElement>("[data-tell-hl]").forEach((el) => {
        el.removeAttribute("data-tell-hl");
        el.style.outline = "";
        el.style.outlineOffset = "";
        el.style.borderRadius = "";
    });
    if (!touches) return;
    const els = touches.flatMap((id) =>
        Array.from(document.querySelectorAll<HTMLElement>(id === "branches" ? '[data-tell="branches"]' : `[data-decision="${CSS.escape(id)}"]`)),
    );
    els.forEach((el) => {
        el.setAttribute("data-tell-hl", "");
        el.style.outline = "2px solid rgb(37 99 235 / 0.6)";
        el.style.outlineOffset = "4px";
        el.style.borderRadius = "1rem";
    });
    if (scroll) els[0]?.scrollIntoView({ behavior: "smooth", block: "center" });
}

export function TellOura({ draft, applier, left, docked = false }: { draft: ScanDraft; applier: Applier; left: number; docked?: boolean }) {
    const [turns, setTurns] = useState<Turn[]>([]);
    const [text, setText] = useState("");
    const [asking, setAsking] = useState(false);
    const next = useRef(1);
    const box = useRef<HTMLElement>(null);
    const input = useRef<HTMLTextAreaElement>(null);
    const tellRunning = applier.plan?.origin === "tell";
    const writesPaused = useWritesPaused();
    useHoldInPlace(box, tellRunning || applier.result?.origin === "tell");
    useEffect(() => () => highlight(null), []);

    // The running answer's panel turns into its result once every step has ticked.
    useEffect(() => {
        if (applier.result?.origin !== "tell" && !(applier.stuck && turns.some((t) => t.who === "oura" && t.state === "applying"))) return;
        setTurns((ts) =>
            ts.map((t) =>
                t.who === "oura" && t.state === "applying"
                    ? applier.stuck
                        ? { id: t.id, who: "oura", state: "note", text: COPY.stuck }
                        : { id: t.id, who: "oura", state: "done", changes: t.changes, before: applier.result?.before ?? left, after: left }
                    : t,
            ),
        );
        if (applier.result?.origin === "tell") applier.dismiss();
    }, [applier.result, applier.stuck]); // eslint-disable-line react-hooks/exhaustive-deps

    // Docked: the newest turn stays in view as the conversation grows.
    const logRef = useRef<HTMLOListElement>(null);
    useEffect(() => {
        if (!docked) return;
        const el = logRef.current?.parentElement;
        if (el) el.scrollTop = el.scrollHeight;
    }, [docked, turns]);

    // The draft on screen now, read by an answer that lands late (Jack, #4451).
    const draftIdRef = useRef(draft.id);
    draftIdRef.current = draft.id;

    const hasOpen = draft.decisions.some((d) => d.state === "open");
    if (!hasOpen && turns.length === 0) return null;

    const say = async (said: string) => {
        const t = said.trim();
        if (!t || asking || applier.running) return;
        // A reading belongs to the draft it was asked about: one that lands after the draft changed is dropped.
        const askedFor = draft.id;
        const you = next.current++;
        const oura = next.current++;
        // An unapplied proposal is set aside once staff say something new.
        setTurns((ts) => [
            ...ts.map((x) => (x.who === "oura" && x.state === "proposal" ? ({ id: x.id, who: "oura", state: "note", text: "Set aside." } as Turn) : x)),
            { id: you, who: "you", text: t },
            { id: oura, who: "oura", state: "reading" },
        ]);
        setText("");
        setAsking(true);
        const reading = await askOura(draft, t);
        if (draftIdRef.current !== askedFor) return;
        setAsking(false);
        setTurns((ts) =>
            ts.map((x) =>
                x.id !== oura
                    ? x
                    : reading.changes.length
                      ? { id: oura, who: "oura", state: "proposal", reading, picked: reading.changes.map((c) => c.id) }
                      : { id: oura, who: "oura", state: "note", text: reading.reply, suggestions: reading.suggestions },
            ),
        );
    };

    const apply = (turnId: number, changes: TellChange[]) => {
        highlight(null);
        const op = (c: TellChange): ApplyOp | null =>
            c.kind === "answer"
                ? { kind: "answer", decisionId: c.decisionId, choice: c.choice, label: c.text }
                : c.kind === "plan"
                  ? { kind: "plan", decisionId: c.decisionId, values: c.values, label: c.text }
                  : null;
        const branches = changes.find((c) => c.kind === "branches");
        const others = changes.filter((c) => c.kind !== "branches");
        applier.apply({
            origin: "tell",
            target: branches && branches.kind === "branches" ? branches.keep : null,
            pre: others.filter((c) => !("after" in c && c.after)).map(op).filter((x): x is ApplyOp => x !== null),
            post: others.filter((c) => "after" in c && c.after).map(op).filter((x): x is ApplyOp => x !== null),
            before: left,
        });
        setTurns((ts) => ts.map((t) => (t.id === turnId ? { id: t.id, who: "oura", state: "applying", changes } : t)));
    };

    const lastDone = [...turns].reverse().find((t) => t.who === "oura" && t.state === "done");

    return (
        <section ref={box} aria-label={COPY.title} className={docked ? "flex min-h-0 flex-1 flex-col" : cn(SURFACE.block, "p-3")}>
            <div className={docked ? "min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4" : undefined}>
            {docked && turns.length === 0 && (
                <div className={cn(TYPE.tb, "flex gap-3 text-gray-700")}>
                    <OuraMark />
                    <div className="min-w-0 flex-1 pt-px">
                        <p className="m-0">{COPY.intro}</p>
                        {/* From this draft, never a fixed sample: another client has no "Lush". */}
                        {tellSuggestions(draft).length > 0 && <Chips items={tellSuggestions(draft)} disabled={writesPaused} onPick={(x) => void say(x)} />}
                    </div>
                </div>
            )}
            {turns.length > 0 && (
                <ol ref={logRef} role="log" aria-live="polite" className={cn("list-plain m-0 space-y-5 p-0", !docked && "mb-4 p-2 pb-0")}>
                    {turns.map((t) =>
                        t.who === "you" ? (
                            <li key={t.id} className="flex justify-end">
                                <p className={cn(TYPE.tb, "m-0 max-w-[80%] rounded-2xl bg-gray-100 px-4 py-2.5 text-gray-900")}>{t.text}</p>
                            </li>
                        ) : (
                            <li key={t.id} className={cn(TYPE.tb, "flex gap-3 text-gray-900")}>
                                <OuraMark busy={t.state === "reading" || t.state === "applying"} />
                                <div className="min-w-0 flex-1 pt-px">
                                    {t.state === "reading" && <p className="m-0 animate-pulse text-gray-500">{COPY.reading}…</p>}

                                    {t.state === "note" && (
                                        <>
                                            <p className="m-0 text-gray-700">{t.text}</p>
                                            {t.suggestions && t.suggestions.length > 0 && <Chips items={t.suggestions} disabled={writesPaused} onPick={(s) => void say(s)} />}
                                        </>
                                    )}

                                    {t.state === "proposal" && (
                                        <>
                                            <p className="m-0">{t.reading.reply}</p>
                                            <ul role="list" className="list-plain m-0 mt-3 divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 p-0">
                                                {t.reading.changes.map((c) => {
                                                    const on = t.picked.includes(c.id);
                                                    return (
                                                        <li
                                                            key={c.id}
                                                            onMouseEnter={() => highlight(c.touches)}
                                                            onMouseLeave={() => highlight(null)}
                                                            className="group flex items-start gap-3 px-3.5 py-3 transition-colors hover:bg-gray-50"
                                                        >
                                                            <Tick
                                                                label={c.text}
                                                                checked={on}
                                                                onChange={() =>
                                                                    setTurns((ts) =>
                                                                        ts.map((x) =>
                                                                            x.id === t.id && x.who === "oura" && x.state === "proposal"
                                                                                ? { ...x, picked: on ? x.picked.filter((p) => p !== c.id) : [...x.picked, c.id] }
                                                                                : x,
                                                                        ),
                                                                    )
                                                                }
                                                            />
                                                            <span className="min-w-0 flex-1">
                                                                <span className={cn("block font-medium transition-colors", on ? "text-gray-900" : "text-gray-400")}>{c.text}</span>
                                                                {c.effect && <span className={cn(TYPE.tc, "mt-0.5 block text-gray-500")}>{c.effect}</span>}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() => highlight(c.touches, true)}
                                                                aria-label={`${COPY.show}: ${c.text}`}
                                                                className={cn(TYPE.tc, "shrink-0 border-0 bg-transparent p-0 text-gray-400 opacity-0 transition-opacity hover:text-gray-700 focus-visible:opacity-100 group-hover:opacity-100")}
                                                            >
                                                                {COPY.show}
                                                            </button>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                            <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-2">
                                                <PillButton
                                                    variant="primary"
                                                    size="sm"
                                                    writes
                                                    disabled={t.picked.length === 0 || applier.running}
                                                    onClick={() => apply(t.id, t.reading.changes.filter((c) => t.picked.includes(c.id)))}
                                                >
                                                    {COPY.apply(t.picked.length)}
                                                </PillButton>
                                                <PillButton
                                                    variant="text"
                                                    size="sm"
                                                    onClick={() => {
                                                        highlight(null);
                                                        setTurns((ts) => ts.map((x) => (x.id === t.id ? { id: x.id, who: "oura", state: "note", text: COPY.notQuiteReply } : x)));
                                                        input.current?.focus();
                                                    }}
                                                >
                                                    {COPY.notQuite}
                                                </PillButton>
                                            </div>
                                        </>
                                    )}

                                    {t.state === "applying" && (
                                        <>
                                            <p className="m-0 mb-3 text-gray-700">{COPY.working}</p>
                                            {tellRunning && <ApplySteps applier={applier} />}
                                        </>
                                    )}

                                    {t.state === "done" && (
                                        <>
                                            <p className="m-0">{COPY.done}</p>
                                            <ul role="list" className="list-plain m-0 mt-2 space-y-1.5 p-0 text-gray-700">
                                                {t.changes.map((c) => (
                                                    <li key={c.id} className="flex items-start gap-2">
                                                        <CheckIcon aria-hidden size={15} strokeWidth={2.5} className="mt-0.5 shrink-0 text-green-600" />
                                                        {c.text}
                                                    </li>
                                                ))}
                                            </ul>
                                            <p className={cn(TYPE.tc, "m-0 mt-2 text-gray-500")}>{COPY.left(t.before, t.after)}</p>
                                            {t === lastDone && applier.canUndo && applier.origin === "tell" && (
                                                <div className="mt-2">
                                                    <PillButton
                                                        variant="text"
                                                        size="sm"
                                                        writes
                                                        busy={applier.undoing}
                                                        onClick={async () => {
                                                            // Said only when the whole batch came back (Jack, #4451).
                                                            const restored = await applier.undoAll();
                                                            if (restored) setTurns((ts) => [...ts, { id: next.current++, who: "oura", state: "note", text: COPY.undone }]);
                                                        }}
                                                    >
                                                        {COPY.undo}
                                                    </PillButton>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </li>
                        ),
                    )}
                </ol>
            )}
            </div>

            <form
                className={docked ? "flex shrink-0 items-end gap-3 border-t border-gray-100 px-4 py-3" : "flex items-center gap-3 p-1"}
                onSubmit={(e) => {
                    e.preventDefault();
                    void say(text);
                }}
            >
                <OuraMark />
                <textarea
                    ref={input}
                    id="tell-oura"
                    aria-label={COPY.title}
                    value={text}
                    onChange={(e) => {
                        setText(e.target.value);
                        // Grows with what is typed, to about five lines, then scrolls: one line never shows a scrollbar.
                        const el = e.currentTarget;
                        el.style.height = "auto";
                        el.style.height = `${Math.min(el.scrollHeight, 128)}px`;
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            if (writesPaused) return;
                            void say(text);
                        }
                    }}
                    rows={1}
                    placeholder={docked ? COPY.dockedPlaceholder : COPY.placeholder}
                    className={cn(
                        TYPE.tb,
                        "max-h-32 min-h-[2.25rem] min-w-0 flex-1 resize-none overflow-y-auto border-0 bg-transparent py-2 text-gray-900 outline-none placeholder:truncate placeholder:text-gray-400",
                    )}
                />
                <button
                    type="submit"
                    aria-label={COPY.send}
                    disabled={!text.trim() || asking || applier.running || writesPaused}
                    className="flex size-8 shrink-0 items-center justify-center rounded-full border-0 bg-blue-600 text-white transition-colors hover:bg-blue-700 disabled:bg-gray-100 disabled:text-gray-300"
                >
                    <ArrowUpIcon aria-hidden size={16} strokeWidth={2.25} />
                </button>
            </form>
        </section>
    );
}

/** Oura's mark beside its turns; it breathes while Oura is reading or working. */
function OuraMark({ busy = false }: { busy?: boolean }) {
    return (
        <span aria-hidden className="flex size-6 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600">
            <SparklesIcon size={13} strokeWidth={2} className={busy ? "animate-pulse" : undefined} />
        </span>
    );
}

function Chips({ items, onPick, disabled }: { items: string[]; onPick(s: string): void; disabled?: boolean }) {
    return (
        <div className="mt-3 flex flex-wrap gap-2">
            {items.map((s) => (
                <button
                    key={s}
                    type="button"
                    disabled={disabled}
                    onClick={() => onPick(s)}
                    className={cn(TYPE.tc, "rounded-full border border-gray-200 bg-white px-3 py-1.5 text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900")}
                >
                    {s}
                </button>
            ))}
        </div>
    );
}

/**
 * Tell Oura on the two-panel Review: a button at the bottom right, above the
 * foot, that opens the conversation as a panel over the page. It opens itself
 * while its changes are being applied, so the steps ticking are seen.
 */
export function TellOuraDock({ draft, applier, left }: { draft: ScanDraft; applier: Applier; left: number }) {
    const [open, setOpen] = useState(false);
    const [used, setUsed] = useState(false);
    const launcher = useRef<HTMLButtonElement>(null);
    const running = applier.plan?.origin === "tell";
    const writesPaused = useWritesPaused();
    useEffect(() => {
        if (running) setOpen(true);
    }, [running]);
    useEffect(() => {
        if (!open) return;
        setUsed(true);
        requestAnimationFrame(() => document.getElementById("tell-oura")?.focus({ preventScroll: true }));
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== "Escape" || e.defaultPrevented) return;
            setOpen(false);
            requestAnimationFrame(() => launcher.current?.focus());
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);
    if (!used && !draft.decisions.some((d) => d.state === "open")) return null;

    return (
        <>
            <div
                role="dialog"
                aria-label={COPY.title}
                hidden={!open}
                // `hidden` alone loses to `flex`: the class carries it too.
                className={cn(
                    "fixed bottom-24 right-4 z-30 max-h-[min(70vh,640px)] w-[min(420px,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/10 sm:right-8",
                    open ? "flex" : "hidden",
                )}
            >
                <div className="flex shrink-0 items-center gap-3 border-b border-gray-100 px-4 py-3">
                    <OuraMark busy={running} />
                    <div className="min-w-0 flex-1">
                        <p className={cn(TYPE.tb, "m-0 font-semibold text-gray-900")}>{COPY.title}</p>
                        <p className={cn(TYPE.tc, "m-0 text-gray-500")}>{COPY.dockSub}</p>
                    </div>
                    <button
                        type="button"
                        aria-label={COPY.close}
                        onClick={() => {
                            setOpen(false);
                            requestAnimationFrame(() => launcher.current?.focus());
                        }}
                        className={cn("grid h-8 w-8 place-items-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-900", FOCUS_RING)}
                    >
                        <XIcon aria-hidden size={16} />
                    </button>
                </div>
                {used && <TellOura draft={draft} applier={applier} left={left} docked />}
            </div>
            {!open && (
                <button
                    ref={launcher}
                    type="button"
                    aria-haspopup="dialog"
                    disabled={writesPaused}
                    onClick={() => setOpen(true)}
                    className={cn(
                        "fixed bottom-24 right-4 z-30 inline-flex h-11 items-center gap-2 rounded-full bg-gray-900 pl-3 pr-4 text-sm font-medium text-white shadow-lg hover:bg-gray-800 sm:right-8",
                        FOCUS_RING,
                    )}
                >
                    <SparklesIcon aria-hidden size={16} />
                    {COPY.open}
                </button>
            )}
        </>
    );
}
